import { useCallback, useEffect, useState } from "react";
import {
  Banner,
  Button,
  ckConfirm,
  Field,
  Icon,
  Panel,
  Select,
  SimpleTable,
  Spinner,
  Switch,
  Tag,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { yemotApi, type YemotBrowse, type YemotConnection, type YemotPreview, type YemotSyncRun, type YemotTracked } from "../../api/salesApi";
import { copyText, errorMessage, fmtDateTime, fmtNum, fmtSecs, withEmpty } from "./salesUi";
import { useSalesMeta } from "./useSalesMeta";
import "./sales.css";

type Configured = Extract<YemotConnection, { configured: true }>;
type ParsedRow = NonNullable<YemotPreview["parsed"][number]> & { i: number };

const RUN_STATUS: Record<string, { label: string; tone: "success" | "danger" | "info" | "neutral" }> = {
  ok: { label: "הצליח", tone: "success" },
  failed: { label: "נכשל", tone: "danger" },
  running: { label: "רץ", tone: "info" },
};

const blankTracked = (): YemotTracked => ({ path: "", label: "", logFile: "", minSeconds: 0, fullSeconds: undefined, createLeads: true, leadStatusKey: null, interestProductId: null, enabled: true });

/** browse the system's folders and preview a log file */
function FileBrowser({ onPick, picking }: { onPick: (file: string) => void; picking: string | null }) {
  const toast = useCkToast();
  const [path, setPath] = useState("/");
  const [data, setData] = useState<YemotBrowse | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ file: string; data: YemotPreview | null; error: string | null } | null>(null);

  const go = useCallback(
    (p: string) => {
      setBusy(true);
      yemotApi
        .browse(p)
        .then((d) => {
          setData(d);
          setPath(d.path || p);
        })
        .catch((e) => toast.error(errorMessage(e, "לא הצלחנו לפתוח את התיקייה")))
        .finally(() => setBusy(false));
    },
    [toast],
  );

  const showPreview = (file: string) => {
    setPreview({ file, data: null, error: null });
    yemotApi
      .preview(file)
      .then((d) => setPreview({ file, data: d, error: null }))
      .catch((e) => setPreview({ file, data: null, error: errorMessage(e, "לא הצלחנו לקרוא את הקובץ") }));
  };

  const parts = path.split("/").filter(Boolean);
  const join = (i: number) => `/${parts.slice(0, i + 1).join("/")}`;
  const sub = (name: string) => `${path === "/" ? "" : path}/${name}`;

  if (!data && !busy) {
    return (
      <Button icon="folder" onClick={() => go("/")}>
        עיון בקבצי המערכת
      </Button>
    );
  }

  const det = preview?.data?.detected;
  const parsed: ParsedRow[] = (preview?.data?.parsed ?? []).slice(0, 10).flatMap((p, i) => (p ? [{ ...p, i }] : []));
  return (
    <div className="sl-browser">
      <div className="sl-row sl-row--between">
        <div className="sl-crumbs" aria-label="נתיב">
          <button type="button" onClick={() => go("/")}>
            ivr2:/
          </button>
          {parts.map((p, i) => (
            <span key={join(i)}>
              <button type="button" onClick={() => go(join(i))}>
                {p}
              </button>
              /
            </span>
          ))}
        </div>
        {busy ? <Spinner size={16} /> : null}
      </div>
      {picking === null ? <p className="sl-muted">כדי למלא קובץ לוג בשלוחה — לחצו «בחירת קובץ» בשורה של השלוחה, ואז «בחר» כאן.</p> : null}
      <ul className="sl-files">
        {path !== "/" ? (
          <li>
            <button type="button" className="sl-files__dir" onClick={() => go(parts.length > 1 ? join(parts.length - 2) : "/")}>
              <Icon name="up" size={14} /> ..
            </button>
          </li>
        ) : null}
        {(data?.dirs ?? []).map((d) => (
          <li key={`d-${d.name}`}>
            <button type="button" className="sl-files__dir" onClick={() => go(sub(d.name))}>
              <Icon name="folder" size={14} />
              <span className="sl-files__name">
                <span>{d.name}</span>
              </span>
              {d.title ? <span className="sl-muted">{d.title}</span> : null}
            </button>
          </li>
        ))}
        {(data?.files ?? []).map((f) => (
          <li key={`f-${f.what}`}>
            <span className="sl-files__name">
              <Icon name="file" size={14} />
              <span title={f.what}>{f.name}</span>
            </span>
            <span className="sl-row">
              {f.mtime ? <span className="sl-muted">{f.mtime}</span> : null}
              <Button size="sm" variant="ghost" icon="eye" onClick={() => showPreview(f.what)}>
                תצוגה מקדימה
              </Button>
              {picking !== null ? (
                <Button size="sm" variant="primary" onClick={() => onPick(f.what)}>
                  בחר
                </Button>
              ) : null}
            </span>
          </li>
        ))}
        {data && !data.dirs.length && !data.files.length ? (
          <li>
            <span className="sl-muted">התיקייה ריקה</span>
          </li>
        ) : null}
      </ul>

      {preview ? (
        <Panel
          title="תצוגה מקדימה"
          sub={<span className="sl-code">{preview.file}</span>}
          icon="eye"
          actions={
            <Button size="sm" variant="ghost" icon="x" iconOnly aria-label="סגירה" onClick={() => setPreview(null)} />
          }
        >
          {preview.error ? <Banner tone="danger">{preview.error}</Banner> : null}
          {!preview.data && !preview.error ? <Spinner size={20} /> : null}
          {preview.data && det ? (
            <div className="sl-stack">
              <span className="sl-muted">{fmtNum(preview.data.total)} שורות בקובץ · עמודות: {preview.data.columns.join(", ") || "—"}</span>
              <div className="sl-cols">
                {(
                  [
                    ["phone", "טלפון"],
                    ["date", "תאריך"],
                    ["time", "שעה"],
                    ["seconds", "משך (שניות)"],
                  ] as Array<[keyof typeof det, string]>
                ).map(([k, l]) => (
                  <Tag key={k} tone={det[k] ? "success" : "danger"} icon={det[k] ? "check" : "x"}>
                    {l}: {det[k] ?? "לא נמצא"}
                  </Tag>
                ))}
              </div>
              <SimpleTable
                ariaLabel="שורות לדוגמה"
                rows={parsed}
                rowKey={(r: ParsedRow) => String(r.i)}
                empty="לא זוהו שורות תקינות"
                columns={[
                  { key: "phone", label: "טלפון", render: (r: ParsedRow) => <span className="sl-ltr">{r.phone}</span> },
                  { key: "startedAt", label: "מתי", render: (r: ParsedRow) => fmtDateTime(r.startedAt) },
                  { key: "seconds", label: "משך", align: "end", render: (r: ParsedRow) => fmtSecs(r.seconds) },
                ]}
              />
            </div>
          ) : null}
        </Panel>
      ) : null}
    </div>
  );
}

/** Settings hub → «ימות המשיח»: connection, tracked extensions, the file browser, sync now and its history. */
export function YemotConnectionPanel() {
  const toast = useCkToast();
  const { optionsOf, productOptions } = useSalesMeta();
  const [conn, setConn] = useState<YemotConnection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [systemNumber, setSystemNumber] = useState("");
  const [token, setToken] = useState("");
  const [authType, setAuthType] = useState<"api_key" | "password">("api_key");
  const [enabled, setEnabled] = useState(true);
  const [syncFrom, setSyncFrom] = useState("");
  const [callerId, setCallerId] = useState("");
  const [tracked, setTracked] = useState<YemotTracked[]>([]);
  const [picking, setPicking] = useState<number | null>(null);
  const [busy, setBusy] = useState<"save" | "test" | "sync" | null>(null);
  const [testInfo, setTestInfo] = useState<string | null>(null);
  const [syncErrors, setSyncErrors] = useState<string[]>([]);
  const [runs, setRuns] = useState<YemotSyncRun[] | null>(null);

  const apply = (c: YemotConnection) => {
    setConn(c);
    if (c.configured) {
      setSystemNumber(c.systemNumber);
      setEnabled(c.enabled);
      setSyncFrom(c.syncFrom ?? "");
      setCallerId(c.callerId ?? "");
      setTracked(c.tracked);
    }
    setToken("");
  };

  const loadRuns = useCallback(() => {
    yemotApi
      .runs()
      .then(setRuns)
      .catch(() => setRuns([]));
  }, []);

  useEffect(() => {
    yemotApi
      .get()
      .then((c) => {
        apply(c);
        if (c.configured) loadRuns();
      })
      .catch((e) => setError(errorMessage(e, "לא הצלחנו לטעון את החיבור")));
  }, [loadRuns]);

  const saved: Configured | null = conn?.configured ? conn : null;
  const patchRow = (i: number, p: Partial<YemotTracked>) => setTracked((arr) => arr.map((t, j) => (j === i ? { ...t, ...p } : t)));

  const save = async () => {
    if (!/^\d{5,20}$/.test(systemNumber.trim())) {
      toast.error("מספר מערכת: ספרות בלבד");
      return;
    }
    if (!saved?.hasToken && !token.trim()) {
      toast.error("צריך מפתח API או סיסמת מערכת");
      return;
    }
    if (callerId.trim() && !/^\d{5,15}$/.test(callerId.trim())) {
      toast.error("מספר מציג: ספרות בלבד");
      return;
    }
    const bad = tracked.find((t) => !t.path.trim() || !t.logFile.trim());
    if (bad) {
      toast.error("לכל שלוחה צריך נתיב וקובץ לוג");
      return;
    }
    setBusy("save");
    try {
      const c = await yemotApi.save({
        systemNumber: systemNumber.trim(),
        token: token.trim() || undefined,
        authType,
        enabled,
        syncFrom: syncFrom || null,
        callerId: callerId.trim() || null,
        tracked: tracked.map((t) => ({ ...t, path: t.path.trim(), label: t.label.trim() || t.path.trim(), logFile: t.logFile.trim() })),
      });
      apply(c);
      toast.success("החיבור נשמר");
      loadRuns();
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(null);
    }
  };

  const test = async () => {
    setBusy("test");
    setTestInfo(null);
    try {
      const r = await yemotApi.test();
      setTestInfo([r.name ? `מערכת: ${r.name}` : null, r.units ? `יחידות: ${r.units}` : null, r.username ? `משתמש: ${r.username}` : null].filter(Boolean).join(" · ") || "החיבור תקין");
      toast.success("החיבור תקין");
    } catch (e) {
      toast.error(errorMessage(e, "החיבור נכשל"));
    } finally {
      setBusy(null);
    }
  };

  const sync = async () => {
    setBusy("sync");
    setSyncErrors([]);
    try {
      const r = await yemotApi.sync();
      setSyncErrors(r.errors ?? []);
      const msg = `נוספו ${fmtNum(r.listensAdded)} שמיעות · ${fmtNum(r.leadsCreated)} לידים חדשים · ${fmtNum(r.leadsUpdated)} לידים עודכנו`;
      if (r.status === "ok") toast.success(msg);
      else toast.error(`המשיכה נכשלה — ${msg}`);
      const c = await yemotApi.get();
      setConn(c);
      loadRuns();
    } catch (e) {
      toast.error(errorMessage(e, "המשיכה נכשלה"));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    if (!(await ckConfirm({ title: "לנתק את ימות המשיח?", message: "החיבור וההגדרות של השלוחות יימחקו. השמיעות והלידים שכבר נמשכו יישארו.", confirmLabel: "ניתוק", danger: true }))) return;
    try {
      await yemotApi.remove();
      setConn({ configured: false });
      setSystemNumber("");
      setTracked([]);
      setRuns(null);
      toast.success("החיבור נותק");
    } catch (e) {
      toast.error(errorMessage(e, "הניתוק נכשל"));
    }
  };

  if (error) return <Banner tone="danger">{error}</Banner>;
  if (!conn) {
    return (
      <div className="sl-busy">
        <Spinner size={22} />
      </div>
    );
  }

  return (
    <div className="sl-stack">
      <p className="sl-note">
        המערכת מושכת מהקו את קבצי הלוג של השלוחות שבחרתם: מי הקשיב, מתי ולכמה זמן. כל טלפון שהקשיב נכנס לרשימת הלידים (או מעדכן ליד קיים) עם תאריך לשיחה חוזרת.
      </p>
      {saved?.lastSyncStatus === "failed" && saved.lastSyncError ? <Banner tone="danger">המשיכה האחרונה נכשלה: {saved.lastSyncError}</Banner> : null}

      <Panel title="חיבור" icon="key">
        <div className="sl-stack">
          <div className="sl-grid2">
            <Field label="מספר מערכת">
              <TextInput ltr inputMode="numeric" value={systemNumber} onChange={(e) => setSystemNumber(e.target.value)} placeholder="0773137770" />
            </Field>
            <Field label="סוג כניסה" hint="מומלץ מפתח API ייעודי (בימות: הגדרות → API), עם הרשאת קבצים ושלוחות">
              <Select
                options={[
                  { value: "api_key", label: "מפתח API" },
                  { value: "password", label: "סיסמת המערכת" },
                ]}
                value={authType}
                onChange={(v) => setAuthType(v === "password" ? "password" : "api_key")}
                aria-label="סוג כניסה"
              />
            </Field>
            <Field label={authType === "password" ? "סיסמת המערכת" : "מפתח API"} hint={saved?.hasToken ? `שמור: ${saved.tokenMasked} — השאירו ריק כדי לא לשנות` : authType === "password" ? "נשמר מוצפן כ«מספר מערכת:סיסמה»" : "המפתח נשמר מוצפן"}>
              <TextInput ltr type="password" autoComplete="new-password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={saved?.hasToken ? saved.tokenMasked : ""} />
            </Field>
            <Field label="למשוך נתונים מתאריך" hint="ריק = מהיום">
              <TextInput type="date" value={syncFrom} onChange={(e) => setSyncFrom(e.target.value)} />
            </Field>
            <Field label="מספר מציג לשיגורים ולחיוג" hint="לצינתוקים, הודעות קוליות וחיוג מכרטיס הליד. ריק = מספר המערכת (או זיהוי מאושר בימות)">
              <TextInput ltr inputMode="numeric" value={callerId} onChange={(e) => setCallerId(e.target.value)} placeholder={systemNumber || "0773137770"} />
            </Field>
            <Field label="משיכה אוטומטית">
              <Switch checked={enabled} onChange={setEnabled} label={enabled ? "פעיל — נמשך כל כמה דקות" : "כבוי"} />
            </Field>
          </div>
          {testInfo ? <Banner tone="success">{testInfo}</Banner> : null}
          <div className="sl-row">
            <Button variant="primary" icon="check" disabled={busy !== null} onClick={() => void save()}>
              {busy === "save" ? "שומר…" : "שמירה"}
            </Button>
            <Button icon="refresh" disabled={!saved || busy !== null} onClick={() => void test()}>
              {busy === "test" ? "בודק…" : "בדיקת חיבור"}
            </Button>
            {saved ? (
              <Button variant="ghost" icon="trash" onClick={() => void disconnect()}>
                ניתוק
              </Button>
            ) : null}
          </div>
        </div>
      </Panel>

      <Panel
        title="שלוחות למעקב"
        icon="mic"
        sub="לכל שלוחה: הנתיב בקו, שם להצגה, וקובץ הלוג שממנו נקראות השמיעות"
        actions={
          <Button size="sm" icon="plus" onClick={() => setTracked((a) => [...a, blankTracked()])}>
            שלוחה
          </Button>
        }
      >
        <div className="sl-stack">
          <Banner tone="info">
            לקובץ לוג יומי כתבו <span className="sl-code">{"{date}"}</span> במקום התאריך — למשל <span className="sl-code">{"ivr2:/Log/LogQueueAll.{date}.ymgr"}</span>. המערכת תקרא קובץ לכל יום.
          </Banner>
          {!tracked.length ? <p className="sl-muted">עוד לא נבחרו שלוחות.</p> : null}
          <div className="sl-tracked">
            {tracked.map((t, i) => (
              <div key={i} className={`sl-tracked__row${picking === i ? " is-editing" : ""}`}>
                <div className="sl-grid3">
                  <Field label="נתיב השלוחה">
                    <TextInput ltr value={t.path} onChange={(e) => patchRow(i, { path: e.target.value })} placeholder="1/2/1/1" />
                  </Field>
                  <Field label="שם להצגה">
                    <TextInput value={t.label} onChange={(e) => patchRow(i, { label: e.target.value })} placeholder="שיעור לדוגמה" />
                  </Field>
                  <Field label="מינימום שניות" hint="הקשבה קצרה מזה לא נספרת">
                    <TextInput ltr inputMode="numeric" value={t.minSeconds == null ? "" : String(t.minSeconds)} onChange={(e) => patchRow(i, { minSeconds: e.target.value === "" ? undefined : Number(e.target.value) || 0 })} />
                  </Field>
                  <Field label="שניות להקשבה מלאה" hint="רשות">
                    <TextInput ltr inputMode="numeric" value={t.fullSeconds == null ? "" : String(t.fullSeconds)} onChange={(e) => patchRow(i, { fullSeconds: e.target.value === "" ? undefined : Number(e.target.value) || 0 })} />
                  </Field>
                  <Field label="סטטוס לליד">
                    <Select options={withEmpty("—", optionsOf("sales_lead_status"))} value={t.leadStatusKey ?? ""} onChange={(v) => patchRow(i, { leadStatusKey: v || null })} aria-label="סטטוס לליד" />
                  </Field>
                  <Field label="מתעניין ב־">
                    <Select options={withEmpty("—", productOptions)} value={t.interestProductId ?? ""} onChange={(v) => patchRow(i, { interestProductId: v || null })} aria-label="מוצר" />
                  </Field>
                </div>
                <Field label="המשך אחרי כניסה (webhook)" hint="לשלוחת type=api: לאן להעביר את המאזין, למשל 1/2/1/1. ריק = בלי webhook">
                  <TextInput ltr value={t.continueTo ?? ""} onChange={(e) => patchRow(i, { continueTo: e.target.value.trim() || null })} placeholder="1/2/1/1" />
                </Field>
                <Field label="קובץ לוג" full>
                  <div className="sl-row">
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <TextInput ltr value={t.logFile} onChange={(e) => patchRow(i, { logFile: e.target.value })} placeholder="בחרו את קובץ הלוג בעיון בתיקיות (תיקיית Log)" />
                    </div>
                    <Button size="sm" icon="folder" variant={picking === i ? "primary" : "default"} disabled={!saved} onClick={() => setPicking(picking === i ? null : i)}>
                      {picking === i ? "בוחרים בעיון למטה…" : "בחירת קובץ"}
                    </Button>
                  </div>
                </Field>
                <div className="sl-row sl-row--between">
                  <div className="sl-row">
                    <Switch checked={t.createLeads !== false} onChange={(v) => patchRow(i, { createLeads: v })} label="ליצור לידים" />
                    <Switch checked={t.enabled !== false} onChange={(v) => patchRow(i, { enabled: v })} label="פעיל" />
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="trash"
                    onClick={() => {
                      setTracked((a) => a.filter((_, j) => j !== i));
                      setPicking(null);
                    }}
                  >
                    הסרה
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {tracked.length ? (
            <div className="sl-row sl-row--end">
              <Button variant="primary" icon="check" disabled={busy !== null} onClick={() => void save()}>
                {busy === "save" ? "שומר…" : "שמירת השלוחות"}
              </Button>
            </div>
          ) : null}
        </div>
      </Panel>

      {saved ? (
        <Panel title="קבצים במערכת" icon="folder" sub="מצאו את קובץ הלוג של השלוחה ובדקו שהעמודות מזוהות">
          <FileBrowser
            picking={picking === null ? null : String(picking)}
            onPick={(file) => {
              if (picking === null) return;
              patchRow(picking, { logFile: file });
              setPicking(null);
              toast.success("הקובץ נבחר — זכרו לשמור");
            }}
          />
        </Panel>
      ) : null}

      {saved ? (
        <Panel
          title="משיכת נתונים"
          icon="refresh"
          sub={saved.lastSyncAt ? `משיכה אחרונה: ${fmtDateTime(saved.lastSyncAt)}${saved.lastSyncStatus ? ` · ${RUN_STATUS[saved.lastSyncStatus]?.label ?? saved.lastSyncStatus}` : ""}` : "עוד לא נמשכו נתונים"}
          actions={
            <Button size="sm" variant="primary" icon="download" disabled={busy !== null || !saved.tracked.length} onClick={() => void sync()}>
              {busy === "sync" ? "מושך…" : "משיכה עכשיו"}
            </Button>
          }
          flush
        >
          {syncErrors.length ? (
            <div style={{ padding: "10px 14px" }}>
              <Banner tone="warning" onClose={() => setSyncErrors([])}>
                {syncErrors.map((e) => (
                  <div key={e}>{e}</div>
                ))}
              </Banner>
            </div>
          ) : null}
          <SimpleTable
            ariaLabel="היסטוריית משיכות"
            loading={runs === null}
            rows={runs ?? []}
            rowKey="id"
            maxHeight={320}
            empty="עוד אין משיכות"
            columns={[
              { key: "startedAt", label: "מתי", render: (r: YemotSyncRun) => fmtDateTime(r.startedAt) },
              { key: "trigger", label: "הפעלה", render: (r: YemotSyncRun) => (r.trigger === "manual" ? "ידנית" : r.trigger === "webhook" ? "Webhook" : "אוטומטית") },
              { key: "status", label: "מצב", render: (r: YemotSyncRun) => <Tag tone={RUN_STATUS[r.status]?.tone ?? "neutral"}>{RUN_STATUS[r.status]?.label ?? r.status}</Tag> },
              { key: "files", label: "קבצים / שורות", align: "end", render: (r: YemotSyncRun) => `${fmtNum(r.filesRead)} / ${fmtNum(r.rowsRead)}` },
              { key: "listens", label: "שמיעות", align: "end", render: (r: YemotSyncRun) => fmtNum(r.listensAdded) },
              { key: "leads", label: "לידים חדשים / עודכנו", align: "end", render: (r: YemotSyncRun) => `${fmtNum(r.leadsCreated)} / ${fmtNum(r.leadsUpdated)}` },
              { key: "error", label: "שגיאה", render: (r: YemotSyncRun) => <span className="sl-muted">{r.error || ""}</span> },
            ]}
          />
        </Panel>
      ) : null}

      {saved?.webhookUrl ? (
        <Panel title="Webhook (רשות)" icon="link">
          <div className="sl-stack">
            <p className="sl-note">
              במקום לחכות למשיכה — אפשר להגדיר בקו שלוחה מסוג <span className="sl-code">type=api</span> עם <span className="sl-code">api_link</span> לכתובת הזו, וכל שמיעה תגיע מיד.
            </p>
            <div className="sl-copy">
              <code>{saved.webhookUrl}</code>
              <Button
                size="sm"
                icon="copy"
                onClick={() => void copyText(saved.webhookUrl ?? "").then((ok) => (ok ? toast.success("הועתק") : toast.error("ההעתקה נכשלה")))}
              >
                העתקה
              </Button>
            </div>
          </div>
        </Panel>
      ) : null}
    </div>
  );
}

export default YemotConnectionPanel;
